import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import apiService from '../api/apiService';
import { useAuth } from './AuthContext';

// 创建记忆上下文
const MemoryContext = createContext();

// 记忆提供者组件
export const MemoryProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [isEnabled, setIsEnabled] = useState(false);
  const [feature1, setFeature1] = useState('');
  const [feature2, setFeature2] = useState('');
  const [loading, setLoading] = useState(false);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);

  // 加载记忆配置 - 使用useCallback避免每次渲染重新创建
  const loadMemoryConfig = useCallback(async (forceReload = false) => {
    // 如果已经加载过且不是强制重新加载，则跳过
    if (isConfigLoaded && !forceReload) return;
    
    // 如果未认证，不执行加载
    if (!isAuthenticated) return;
    
    try {
      setLoading(true);
      console.log('Loading memory config...');
      const response = await apiService.getMemoryConfig();
      
      if (response.code === 200 && response.data) {
        // enable字段现在是int类型：0表示开启，1表示关闭
        setIsEnabled(response.data.enable === 0);
        setFeature1(response.data.feature1 || '');
        setFeature2(response.data.feature2 || '');
        setIsConfigLoaded(true);
        console.log('Memory config loaded successfully:', response.data);
      } else {
        console.warn('Failed to load memory config:', response);
      }
    } catch (error) {
      console.error('Failed to load memory config:', error);
    } finally {
      setLoading(false);
    }
  }, [isConfigLoaded, isAuthenticated]);

  // 保存记忆配置
  const saveMemoryConfig = async (newFeature1, newFeature2) => {
    try {
      setLoading(true);
      // enable字段现在是int类型：0表示开启，1表示关闭
      const enableValue = isEnabled ? 0 : 1;
      
      // 如果传入了新的feature值，使用传入的值；否则使用当前状态值
      const f1 = newFeature1 !== undefined ? newFeature1 : feature1;
      const f2 = newFeature2 !== undefined ? newFeature2 : feature2;
      
      const response = await apiService.saveMemoryConfig(enableValue, f1, f2);
      
      return response.code === 200;
    } catch (error) {
      console.error('Failed to save memory config:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 切换启用状态
  const toggleEnabled = () => {
    setIsEnabled(prev => !prev);
  };

  // 重置配置状态
  const resetMemoryConfig = useCallback(() => {
    setIsEnabled(false);
    setFeature1('');
    setFeature2('');
    setIsConfigLoaded(false);
    setLoading(false);
  }, []);

  // 初始化加载配置 - 在已认证时立即加载
  useEffect(() => {
    if (isAuthenticated) {
      loadMemoryConfig();
    } else {
      // 用户登出时重置配置
      resetMemoryConfig();
    }
  }, [isAuthenticated, loadMemoryConfig, resetMemoryConfig]);

  return (
    <MemoryContext.Provider value={{
      isEnabled,
      feature1,
      feature2,
      loading,
      setFeature1,
      setFeature2,
      toggleEnabled,
      loadMemoryConfig,
      saveMemoryConfig,
      resetMemoryConfig,
      isConfigLoaded
    }}>
      {children}
    </MemoryContext.Provider>
  );
};

// 自定义Hook，方便在组件中使用记忆上下文
export const useMemory = () => useContext(MemoryContext);

export default MemoryContext; 